import React, { useMemo, useState } from 'react'
import Typography from '@material-ui/core/Typography'
import Box from '@material-ui/core/Box'
import { signOut } from 'store/auth'
import authSlice from 'store/auth'
import { makeStyles } from '@material-ui/core/styles'
import { useHistory } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import AppbarLogo from 'assets/white_logo.png'
import Menu from '@material-ui/core/Menu'
import MenuItem from '@material-ui/core/MenuItem'
import ListItemText from '@material-ui/core/ListItemText'
import ExitToAppIcon from '@material-ui/icons/ExitToApp'
import LockIcon from '@material-ui/icons/Lock'
import SwapHorizIcon from '@material-ui/icons/SwapHoriz'
import Avatar from '@material-ui/core/Avatar'
import IconButton from '@material-ui/core/IconButton'
import MenuIcon from '@material-ui/icons/Menu'
import Chip from '@material-ui/core/Chip'
import Divider from '@material-ui/core/Divider'
import AccessTimeIcon from '@material-ui/icons/AccessTime'
import Tooltip from '@material-ui/core/Tooltip'

import { getDefaultTimezone } from 'utils/times'
import { Roles } from 'constants/index'
import { getConsoleHome } from 'routes'
import ChangePasswordModal from './ChangePasswordModal'
import { fetchConferenceById } from 'store/conference'
import { fetchSettings } from 'store/settings'

const TOPBAR_HEIGHT = 64

const DesktopActionBar = ({ onToggleSidebar }) => {
  const history = useHistory()
  const dispatch = useDispatch()
  const classes = useStyles()
  const { user, conferenceAssignments } = useSelector((state) => state.auth)
  const [anchorEl, setAnchorEl] = useState(null)
  const [changePasswordOpen, setChangePasswordOpen] = useState(false)
  const [switcherAnchorEl, setSwitcherAnchorEl] = useState(null)

  const isMultiConference = conferenceAssignments?.length > 1

  const handleSwitcherOpen = (event) => setSwitcherAnchorEl(event.currentTarget)
  const handleSwitcherClose = () => setSwitcherAnchorEl(null)

  const handleConferenceSwitch = (assignment) => {
    handleSwitcherClose()
    if (assignment.conferenceId === user?.conferenceId) return
    localStorage.setItem(`selectedConferenceId_${user.id}`, assignment.conferenceId)
    dispatch(
      authSlice.actions.switchConference({
        conferenceId: assignment.conferenceId,
        role: assignment.role
      })
    )
    dispatch(fetchConferenceById(assignment.conferenceId))
    dispatch(fetchSettings(assignment.conferenceId))
    history.replace(getConsoleHome({ ...user, conferenceId: assignment.conferenceId }))
  }

  const { conference } = useSelector((state) => state.conference)

  const defaultTimezone = useMemo(() => getDefaultTimezone(conference?.timezone), [conference])

  const isSuperAdmin = user?.role === Roles.SUPER_ADMIN
  const isOrganizer = user?.role === Roles.ORGANIZER

  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget)
  const handleMenuClose = () => setAnchorEl(null)

  const handleChangePassword = () => {
    handleMenuClose()
    setChangePasswordOpen(true)
  }

  const handleLogout = async () => {
    handleMenuClose()
    await dispatch(signOut())
    localStorage.removeItem('badgeOrientation')
    history.push('/')
  }

  const barBg = isSuperAdmin
    ? 'linear-gradient(135deg, #f8ab26 0%, #fbbf24 100%)'
    : isOrganizer
    ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
    : 'linear-gradient(135deg, #22b1f3 0%, #15b4db 100%)'

  const textColor = '#ffffff'

  return (
    <Box className={classes.topbar} style={{ background: barBg }}>
      {/* Left section */}
      <Box className={classes.leftSection}>
        <IconButton
          size="small"
          onClick={onToggleSidebar}
          className={classes.menuButton}
          style={{ color: textColor }}>
          <MenuIcon fontSize="small" />
        </IconButton>
        <img
          className={classes.logo}
          alt="Meetsymple"
          src={AppbarLogo}
          onClick={() => history.push(getConsoleHome(user))}
        />
      </Box>

      {/* Center section — conference title */}
      <Box className={classes.centerSection}>
        {!isSuperAdmin && !isOrganizer && (
          <Typography className={classes.title} style={{ color: textColor }}>
            {conference?.confName || 'Admin Panel'}
          </Typography>
        )}
        <Chip
          label={
            isSuperAdmin
              ? 'Super Admin Portal'
              : isOrganizer
              ? 'Conference Organizer Portal'
              : 'Admin Portal'
          }
          size="small"
          className={classes.portalChip}
          style={{
            backgroundColor: 'rgba(255,255,255,0.18)',
            color: '#ffffff'
          }}
        />
      </Box>

      {/* Right section */}
      <Box className={classes.rightSection}>
        {!isSuperAdmin && !isOrganizer && (
          <Chip
            icon={<AccessTimeIcon style={{ fontSize: 14, color: textColor, opacity: 0.85 }} />}
            label={`${conference?.timezone ?? defaultTimezone}`}
            size="small"
            className={classes.timezoneChip}
            style={{ color: textColor, borderColor: `${textColor}40` }}
          />
        )}
        {isMultiConference && (
          <>
            <Tooltip title="Switch Conference">
              <IconButton
                size="small"
                onClick={handleSwitcherOpen}
                className={classes.switcherButton}
                style={{ color: '#ffffff' }}>
                <SwapHorizIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={switcherAnchorEl}
              open={Boolean(switcherAnchorEl)}
              onClose={handleSwitcherClose}
              classes={{ paper: classes.switcherMenu }}
              getContentAnchorEl={null}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
              <Box className={classes.switcherHeader}>
                <Typography className={classes.switcherTitle}>Switch Conference</Typography>
              </Box>
              <Divider />
              {conferenceAssignments.map((assignment) => {
                const role = assignment.role ?? 'admin'
                const isOwner = role === 'owner'
                const isExpired = assignment.isExpired
                return (
                  <Tooltip
                    key={assignment.conferenceId}
                    title={
                      isExpired ? 'This conference has expired and is no longer accessible.' : ''
                    }
                    placement="left">
                    <MenuItem
                      onClick={() => !isExpired && handleConferenceSwitch(assignment)}
                      selected={!isExpired && assignment.conferenceId === user?.conferenceId}
                      className={`${classes.switcherItem} ${
                        isExpired ? classes.switcherItemExpired : ''
                      }`}>
                      <ListItemText
                        primary={assignment.confName}
                        primaryTypographyProps={{
                          className: classes.switcherItemText,
                          style: isExpired ? { color: '#94a3b8' } : {}
                        }}
                      />
                      <Box style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <Chip
                          label={role.charAt(0).toUpperCase() + role.slice(1)}
                          size="small"
                          style={{
                            backgroundColor: isOwner ? '#dbeafe' : '#f1f5f9',
                            color: isOwner ? '#1d4ed8' : '#475569',
                            fontWeight: 600,
                            fontSize: 11,
                            height: 20,
                            opacity: isExpired ? 0.5 : 1
                          }}
                        />
                        {isExpired ? (
                          <Chip
                            label="Expired"
                            size="small"
                            style={{
                              backgroundColor: '#fef2f2',
                              color: '#ef4444',
                              fontWeight: 600,
                              fontSize: 11,
                              height: 20
                            }}
                          />
                        ) : (
                          assignment.conferenceId === user?.conferenceId && (
                            <Chip label="Active" size="small" className={classes.activeChip} />
                          )
                        )}
                      </Box>
                    </MenuItem>
                  </Tooltip>
                )
              })}
            </Menu>
          </>
        )}

        {user && (
          <>
            <Box className={classes.avatarWrapper} onClick={handleMenuOpen}>
              <Avatar className={classes.avatar} alt={user.fullName || user.email} src="#">
                {user.fullName?.[0] || user.email?.[0]?.toUpperCase()}
              </Avatar>
              <Box className={classes.avatarInfo}>
                <Typography className={classes.avatarName} style={{ color: textColor }}>
                  {user.fullName?.split(' ')[0] ||
                    (isSuperAdmin ? 'Super Admin' : isOrganizer ? 'Organizer' : 'User')}
                </Typography>
              </Box>
            </Box>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              classes={{ paper: classes.profileMenu }}
              getContentAnchorEl={null}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
              {/* Profile header */}
              <Box className={classes.userInfo}>
                <Avatar className={classes.menuAvatar} alt={user.fullName || user.email} src="#">
                  {user.fullName?.[0] || user.email?.[0]?.toUpperCase()}
                </Avatar>
                <Box style={{ flex: 1, minWidth: 0 }}>
                  <Typography className={classes.userName} noWrap>
                    {user.fullName || (isSuperAdmin ? 'Super Admin' : 'User')}
                  </Typography>
                  <Typography className={classes.userEmail} noWrap>
                    {user.email}
                  </Typography>
                  <Chip
                    label={
                      isSuperAdmin ? 'Super Admin' : isOrganizer ? 'Conference Organizer' : 'Admin'
                    }
                    size="small"
                    className={classes.roleChip}
                  />
                </Box>
              </Box>
              <Divider style={{ margin: '4px 0' }} />
              {/* Actions */}
              {!isSuperAdmin && !isOrganizer && (
                <MenuItem onClick={handleChangePassword} className={classes.menuItem}>
                  <Box className={classes.menuIconBox} style={{ backgroundColor: '#eff6ff' }}>
                    <LockIcon style={{ fontSize: 16, color: '#3b82f6' }} />
                  </Box>
                  <ListItemText
                    primary="Change Password"
                    primaryTypographyProps={{ className: classes.menuItemText }}
                  />
                </MenuItem>
              )}
              <MenuItem onClick={handleLogout} className={classes.menuItem}>
                <Box className={classes.menuIconBox} style={{ backgroundColor: '#fef2f2' }}>
                  <ExitToAppIcon style={{ fontSize: 16, color: '#ef4444' }} />
                </Box>
                <ListItemText
                  primary="Sign Out"
                  primaryTypographyProps={{ className: classes.menuItemTextDanger }}
                />
              </MenuItem>
            </Menu>
            <ChangePasswordModal
              open={changePasswordOpen}
              onClose={() => setChangePasswordOpen(false)}
            />
          </>
        )}
      </Box>
    </Box>
  )
}

const AdminTopBar = ({ onToggleSidebar }) => {
  return <DesktopActionBar onToggleSidebar={onToggleSidebar} />
}

export default AdminTopBar
export { TOPBAR_HEIGHT }

const useStyles = makeStyles((theme) => ({
  topbar: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    height: TOPBAR_HEIGHT,
    zIndex: theme.zIndex.drawer + 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingLeft: 12,
    paddingRight: 20,
    boxShadow: '0 2px 12px rgba(0,0,0,0.15)'
  },
  leftSection: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flex: '0 0 auto',
    position: 'relative',
    zIndex: 1
  },
  centerSection: {
    position: 'absolute',
    left: '50%',
    transform: 'translateX(-50%)',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    pointerEvents: 'none'
  },
  rightSection: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    flex: '0 0 auto',
    position: 'relative',
    zIndex: 1
  },
  menuButton: {
    padding: 6,
    borderRadius: 8,
    '&:hover': {
      backgroundColor: 'rgba(255,255,255,0.15)'
    }
  },
  logo: {
    height: 42,
    width: 90,
    cursor: 'pointer',
    objectFit: 'contain',
    filter: 'brightness(1) drop-shadow(0 1px 2px rgba(0,0,0,0.1))'
  },
  superAdminChip: {
    backgroundColor: 'rgba(255,255,255,0.2)',
    color: '#ffffff',
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: '0.06em',
    height: 20,
    borderRadius: 4
  },
  title: {
    fontSize: 16,
    fontWeight: 600,
    letterSpacing: '0.01em',
    whiteSpace: 'nowrap',
    textShadow: '0 1px 2px rgba(0,0,0,0.1)',
    marginRight: 8
  },
  portalChip: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.05em',
    height: 24,
    borderRadius: 12,
    border: '1px solid rgba(255,255,255,0.3)',
    backdropFilter: 'blur(4px)',
    textTransform: 'uppercase'
  },
  timezoneChip: {
    backgroundColor: 'rgba(255,255,255,0.15)',
    border: '1px solid',
    fontSize: 12,
    fontWeight: 500,
    height: 28,
    backdropFilter: 'blur(4px)',
    '& .MuiChip-icon': {
      marginLeft: 8
    }
  },
  avatarWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    cursor: 'pointer',
    padding: '4px 8px 4px 4px',
    borderRadius: 24,
    backgroundColor: 'rgba(255,255,255,0.15)',
    backdropFilter: 'blur(4px)',
    transition: 'background-color 0.2s',
    '&:hover': {
      backgroundColor: 'rgba(255,255,255,0.25)'
    }
  },
  avatar: {
    width: 32,
    height: 32,
    fontSize: 14,
    fontWeight: 600,
    backgroundColor: 'rgba(255,255,255,0.3)',
    color: 'inherit',
    border: '2px solid rgba(255,255,255,0.5)'
  },
  avatarName: {
    fontSize: 13,
    fontWeight: 600,
    lineHeight: 1
  },
  profileMenu: {
    minWidth: 260,
    borderRadius: 14,
    boxShadow: '0 12px 40px rgba(0,0,0,0.14)',
    marginTop: 8,
    overflow: 'hidden',
    border: '1px solid #e2e8f0'
  },
  userInfo: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 12,
    padding: '16px 16px 12px',
    background: 'linear-gradient(135deg, #f0f9ff 0%, #e8f4fd 100%)'
  },
  menuAvatar: {
    width: 44,
    height: 44,
    fontSize: 18,
    fontWeight: 700,
    background: 'linear-gradient(135deg, #0ea5e9, #15b4db)',
    color: '#fff',
    flexShrink: 0,
    boxShadow: '0 2px 8px rgba(14,165,233,0.35)'
  },
  userName: {
    fontWeight: 700,
    fontSize: 14,
    color: '#0f172a',
    lineHeight: 1.3
  },
  userEmail: {
    fontSize: 12,
    color: '#64748b',
    lineHeight: 1.4,
    marginTop: 2
  },
  roleChip: {
    marginTop: 6,
    height: 20,
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: '0.04em',
    backgroundColor: 'rgba(14,165,233,0.12)',
    color: '#0284c7',
    borderRadius: 4
  },
  menuItem: {
    padding: '10px 14px',
    margin: '2px 6px',
    borderRadius: 8,
    transition: 'background-color 0.15s',
    '&:hover': {
      backgroundColor: '#f1f5f9'
    }
  },
  menuIconBox: {
    width: 30,
    height: 30,
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 10,
    flexShrink: 0
  },
  menuItemText: {
    fontSize: 13,
    fontWeight: 500,
    color: '#334155'
  },
  menuItemTextDanger: {
    fontSize: 13,
    fontWeight: 500,
    color: '#ef4444'
  },
  switcherButton: {
    padding: 6,
    borderRadius: 8,
    backgroundColor: 'rgba(255,255,255,0.15)',
    '&:hover': {
      backgroundColor: 'rgba(255,255,255,0.25)'
    }
  },
  switcherMenu: {
    minWidth: 240,
    borderRadius: 12,
    boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
    border: '1px solid #e2e8f0',
    marginTop: 8
  },
  switcherHeader: {
    padding: '12px 16px 8px'
  },
  switcherTitle: {
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: '#94a3b8'
  },
  switcherItem: {
    padding: '10px 16px',
    '&.Mui-selected': {
      backgroundColor: '#f0f9ff'
    },
    '&:hover': {
      backgroundColor: '#f1f5f9'
    }
  },
  switcherItemExpired: {
    cursor: 'not-allowed',
    opacity: 0.6,
    '&:hover': {
      backgroundColor: 'transparent'
    }
  },
  switcherItemText: {
    fontSize: 13,
    fontWeight: 500,
    color: '#1e293b'
  },
  activeChip: {
    height: 18,
    fontSize: 10,
    fontWeight: 700,
    backgroundColor: '#dbeafe',
    color: '#1d4ed8',
    borderRadius: 4,
    marginLeft: 8
  }
}))
